import { InspectorControls, useBlockProps }                                            from '@wordpress/block-editor';
import { ColorPalette, PanelBody, RangeControl, SelectControl, TabPanel, TextControl } from '@wordpress/components';
import { useEffect, useRef, useState }                                                 from '@wordpress/element';
import { useSelect }                                                                   from '@wordpress/data';
import { __ }                                                                          from '@wordpress/i18n';
import QRCodeStyling                                                                   from 'qr-code-styling';

/**
 *
 * @param attributes
 * @param setAttributes
 * @returns {JSX.Element}
 * @constructor
 */
function Edit( { attributes, setAttributes } ) {
	const {
		value,
		size,
		margin,
		typeNumber,
		errorCorrectionLevel,
		bgColor
	} = attributes;
	const qrRef = useRef( null );
	const qrCode = useRef( null );
	const blockProps = useBlockProps( { className: 'qr-code-wrapper' } );
	const post = useSelect( ( select ) => select( 'core/editor' )?.getCurrentPost(), [] );
	const data = { attributes, post };
	const upgradeLink = window.qrcodegenerator?.upgradeLink || '';
	const [ activeTab, setActiveTab ] = useState( 'settings' );
	const updateQR = () => {
		if ( !qrCode.current ) return;

		qrCode.current?.update( getQRCodeSettings( data ) );
		qrCode.current?.append( qrRef.current );
	};

	useEffect( () => {
		qrCode.current = new QRCodeStyling( getQRCodeSettings( data ) );
		qrCode.current?.append( qrRef.current );

		return () => {
			qrRef.current && (qrRef.current.innerHTML = '');
		};
	}, [] );

	useEffect( () => {
		updateQR();
	}, [
		value,
		size,
		margin,
		typeNumber,
		errorCorrectionLevel,
		bgColor,
	] );

	return (
		<>
			<InspectorControls>
				<TabPanel
					className="qr-code-settings-tabs"
					onSelect={ ( tabName ) => setActiveTab( tabName ) }
					initialTabName={ activeTab }
					tabs={ [
						{
							name: 'settings',
							title: __( 'QR Code Settings', 'qr-code-generator-by-kodionica' ),
							className: 'qr-code-settings-tab',
							initialOpen: true,
						},
						{
							name: 'pro',
							title: __( 'PRO Features', 'qr-code-generator-by-kodionica' ),
							className: 'qr-code-pro-tab',
							initialOpen: true,
						}
					] }>
					{ ( tab ) => (
						<PanelBody title={ tab.title } initialOpen={ tab.initialOpen }>
							{ tab.name === 'settings' &&
								<>
									<TextControl
										label={ __( 'QR Code Value', 'qr-code-generator-by-kodionica' ) }
										value={ value }
										onChange={ ( val ) => setAttributes( { value: val } ) }
										help={ __( 'Enter the value or URL for the QR code. If left empty, it will use the post link.', 'qr-code-generator-by-kodionica' ) }
										__nextHasNoMarginBottom
										__next40pxDefaultSize
									/>
									<RangeControl
										label={ __( 'QR Code Size', 'qr-code-generator-by-kodionica' ) }
										value={ size }
										onChange={ ( val ) => setAttributes( { size: val } ) }
										min={ 50 }
										max={ 500 }
										__nextHasNoMarginBottom
										__next40pxDefaultSize
									/>
									<RangeControl
										label={ __( 'Margin', 'qr-code-generator-by-kodionica' ) }
										value={ margin }
										onChange={ ( val ) => setAttributes( { margin: val } ) }
										min={ 0 }
										max={ 100 }
										__nextHasNoMarginBottom
										__next40pxDefaultSize
									/>
									<RangeControl
										label={ __( 'QR Code type style', 'qr-code-generator-by-kodionica' ) }
										value={ typeNumber }
										onChange={ ( val ) => setAttributes( { typeNumber: val } ) }
										min={ 3 }
										max={ 15 }
										__nextHasNoMarginBottom
										__next40pxDefaultSize
									/>
									<SelectControl
										label={ __( 'Error Correction Level', 'qr-code-generator-by-kodionica' ) }
										value={ errorCorrectionLevel }
										options={ [
											{ label: 'L (Low)', value: 'L' },
											{ label: 'M (Medium)', value: 'M' },
											{ label: 'Q (Quartile)', value: 'Q' },
											{ label: 'H (High)', value: 'H' }
										] }
										onChange={ ( val ) => setAttributes( { errorCorrectionLevel: val } ) }
										__nextHasNoMarginBottom
										__next40pxDefaultSize
									/>
									<ColorPalette
										label={ __( 'Background color', 'qr-code-generator-by-kodionica' ) }
										value={ bgColor }
										onChange={ ( val ) => setAttributes( { bgColor: val } ) }
									/>
								</>
							}
							{ tab.name === 'pro' &&
								<>
									<p>{ __( 'For more advanced features, consider upgrading to the PRO version.', 'qr-code-generator-by-kodionica' ) }</p>
									<p>{ __( 'The PRO version includes:', 'qr-code-generator-by-kodionica' ) }</p>
									<ul>
										<li>{ __( 'Custom logos in QR codes', 'qr-code-generator-by-kodionica' ) }</li>
										<li>{ __( 'Dynamic QR codes', 'qr-code-generator-by-kodionica' ) }</li>
										<li>{ __( 'Advanced styling options', 'qr-code-generator-by-kodionica' ) }</li>
										<li>{ __( 'Analytics and tracking', 'qr-code-generator-by-kodionica' ) }</li>
										<li>{ __( 'QR Code shape', 'qr-code-generator-by-kodionica' ) }</li>
										<li>{ __( 'QR Code dots options', 'qr-code-generator-by-kodionica' ) }</li>
										<li>{ __( 'QR Code corners options', 'qr-code-generator-by-kodionica' ) }</li>
										<li>{ __( 'And more...', 'qr-code-generator-by-kodionica' ) }</li>
									</ul>
									{
										upgradeLink &&
										<p style={ { fontSize: '11px', textAlign: 'right' } }>
											<a href={ upgradeLink } target="_blank" rel="noopener noreferrer">
												Upgrade to PRO →
											</a>
										</p>
									}
								</> }
						</PanelBody>
					) }
				</TabPanel>
			</InspectorControls>
			<div { ...blockProps }>
				<div ref={ qrRef }></div>
			</div>
		</>
	);
}

function getQRCodeSettings( { attributes, post, media } ) {
	const { value, size, margin, typeNumber, errorCorrectionLevel, bgColor } = attributes;

	return {
		'type': 'canvas',
		'data': value || post.link,
		'width': size,
		'height': size,
		margin,
		qrOptions: {
			typeNumber,
			errorCorrectionLevel
		},
		backgroundOptions: {
			'color': bgColor
		},
	};
}

export default Edit;
